import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { adminApi } from '@/lib/api';
import {
    Users,
    Mail,
    Heart,
    Briefcase,
    Package,
    Newspaper,
    Camera,
    Star,
    Handshake,
    Download,
    TrendingUp,
    Trophy,
    ArrowRight,
} from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, sub, to }) => {
    const inner = (
        <Card className="h-full bg-card border-border card-lift">
            <CardContent className="p-5">
                <div className="flex items-center justify-between">
                    <div className="h-10 w-10 rounded-lg bg-[hsl(var(--primary))]/8 text-[hsl(var(--primary))] flex items-center justify-center">
                        <Icon className="h-5 w-5" />
                    </div>
                    {to && (
                        <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                </div>
                <div className="mt-3 font-display font-semibold text-3xl tabular-nums">
                    {value ?? '—'}
                </div>
                <div className="text-xs text-muted-foreground">{label}</div>
                {sub && (
                    <div className="mt-1 text-xs text-[hsl(var(--accent))] font-semibold">
                        {sub}
                    </div>
                )}
            </CardContent>
        </Card>
    );
    return to ? (
        <Link to={to} className="group">
            {inner}
        </Link>
    ) : (
        inner
    );
};

export default function AdminDashboardPage() {
    const [data, setData] = useState(null);
    useEffect(() => {
        adminApi.dashboard().then(setData).catch(() => {});
    }, []);

    if (!data) {
        return (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {Array.from({ length: 8 }).map((_, i) => (
                    <Skeleton key={i} className="h-32" />
                ))}
            </div>
        );
    }

    const c = data.counts;

    return (
        <div data-testid="admin-dashboard">
            <div className="mb-6">
                <h1 className="font-display text-2xl font-semibold">Dashboard</h1>
                <p className="text-sm text-muted-foreground">
                    Overview of content and recent submissions.
                </p>
            </div>

            {/* Submissions */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                <StatCard
                    icon={Users}
                    label="Volunteer Registrations"
                    value={c.volunteers_total}
                    sub={c.volunteers_unread ? `${c.volunteers_unread} unread` : 'All read'}
                    to="/admin/volunteers"
                />
                <StatCard
                    icon={Mail}
                    label="Contact Submissions"
                    value={c.contacts_total}
                    sub={c.contacts_unread ? `${c.contacts_unread} unread` : 'All read'}
                    to="/admin/contacts"
                />
                <StatCard
                    icon={Heart}
                    label="Donation Inquiries"
                    value={c.donations_total}
                    sub={c.donations_unread ? `${c.donations_unread} unread` : 'All read'}
                    to="/admin/donations"
                />
            </div>

            {/* Content counts */}
            <div className="mb-3">
                <div className="font-semibold">Content</div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={Briefcase} label="Services" value={c.services} to="/admin/services" />
                <StatCard icon={Package} label="Projects" value={c.projects} to="/admin/projects" />
                <StatCard
                    icon={Newspaper}
                    label="News & Events"
                    value={c.news}
                    to="/admin/news"
                />
                <StatCard
                    icon={Star}
                    label="Testimonials"
                    value={c.testimonials}
                    to="/admin/testimonials"
                />
                <StatCard icon={Camera} label="Gallery Items" value={c.gallery} to="/admin/gallery" />
                <StatCard
                    icon={Handshake}
                    label="Partners"
                    value={c.partners}
                    to="/admin/partners"
                />
                <StatCard
                    icon={Download}
                    label="Downloads"
                    value={c.downloads}
                    to="/admin/downloads"
                />
                <StatCard
                    icon={TrendingUp}
                    label="Impact Stats"
                    value={c.impact_stats}
                    to="/admin/impact-stats"
                />
            </div>

            {/* Recent */}
            <div className="mt-8 grid lg:grid-cols-2 gap-4">
                <Card className="bg-card border-border">
                    <CardContent className="p-5">
                        <div className="flex items-center justify-between mb-3">
                            <div className="font-semibold">Recent volunteer signups</div>
                            <Link
                                to="/admin/volunteers"
                                className="text-xs text-[hsl(var(--primary))] font-semibold hover:underline"
                            >
                                View all
                            </Link>
                        </div>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Name</TableHead>
                                    <TableHead>City</TableHead>
                                    <TableHead>Date</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {data.recent_volunteers?.length ? (
                                    data.recent_volunteers.map((v) => (
                                        <TableRow key={v.id}>
                                            <TableCell className="font-medium">{v.name}</TableCell>
                                            <TableCell>{v.city || '—'}</TableCell>
                                            <TableCell className="text-xs">
                                                {v.created_at?.slice(0, 10)}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell
                                            colSpan={3}
                                            className="text-center py-6 text-muted-foreground text-sm"
                                        >
                                            No signups yet
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
                <Card className="bg-card border-border">
                    <CardContent className="p-5">
                        <div className="flex items-center justify-between mb-3">
                            <div className="font-semibold">Recent contact messages</div>
                            <Link
                                to="/admin/contacts"
                                className="text-xs text-[hsl(var(--primary))] font-semibold hover:underline"
                            >
                                View all
                            </Link>
                        </div>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Subject</TableHead>
                                    <TableHead>Date</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {data.recent_contacts?.length ? (
                                    data.recent_contacts.map((v) => (
                                        <TableRow key={v.id}>
                                            <TableCell className="font-medium">{v.name}</TableCell>
                                            <TableCell className="truncate max-w-[200px]">
                                                {v.subject || '—'}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                                {v.created_at?.slice(0, 10)}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell
                                            colSpan={3}
                                            className="text-center py-6 text-muted-foreground text-sm"
                                        >
                                            No messages yet
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
